import { useState, useRef, useEffect, useCallback, useMemo } from "react";
import s from "./StoryRecap.module.css";
import { useAiChat } from "../AiChatContext";
import { AiSparkleIcon } from "../components/AiChatPopup";
import FeedbackCard from "../components/FeedbackCard";
import { StoryCinema } from "../cinema";
import { apiFetch } from "../../../../api";
import { trackEvent } from "../../../../analytics/posthog";
import { useWordSave } from "../hooks/useWordSave";

const BACKEND = process.env.REACT_APP_BACKEND_URL;

const PAUSE_BETWEEN_STEPS = 1200;

/* ═══════════════════════════════════
   Audio hooks
   ═══════════════════════════════════ */

function useAudioPlayer() {
  const audioRef = useRef(null);
  const [playing, setPlaying] = useState(false);

  const play = useCallback((storyId, audioPath, playbackRate = 1.0) => {
    if (audioRef.current) { audioRef.current.pause(); audioRef.current = null; }
    const url = `${BACKEND}/api/v1/admin/audio/${storyId}/${audioPath}`;
    const audio = new Audio(url);
    audio.playbackRate = playbackRate;
    audio.onended = () => { setPlaying(false); audioRef.current = null; };
    audio.onerror = () => { setPlaying(false); audioRef.current = null; };
    audio.play().catch(() => { setPlaying(false); alert("Audio not generated yet."); });
    audioRef.current = audio;
    setPlaying(true);
  }, []);

  const pause = useCallback(() => {
    if (audioRef.current && !audioRef.current.paused) {
      audioRef.current.pause();
      setPlaying(false);
    }
  }, []);

  const resume = useCallback(() => {
    if (audioRef.current && audioRef.current.paused && audioRef.current.src) {
      audioRef.current.play().catch(() => {});
      setPlaying(true);
    }
  }, []);

  const stop = useCallback(() => {
    if (audioRef.current) { audioRef.current.pause(); audioRef.current = null; }
    setPlaying(false);
  }, []);

  const isPaused = !playing && audioRef.current && audioRef.current.paused && audioRef.current.src;

  useEffect(() => () => { if (audioRef.current) audioRef.current.pause(); }, []);

  return { play, pause, resume, stop, playing, isPaused };
}

function useWordAudio() {
  const audioRef = useRef(null);
  const generationRef = useRef(0);
  const [loadingWordId, setLoadingWordId] = useState(null);

  const playWord = useCallback((wordId, filename, playbackRate = 1.0) => {
    if (audioRef.current) { audioRef.current.pause(); audioRef.current = null; }
    const generation = ++generationRef.current;
    const key = `${wordId}/${filename}`;
    setLoadingWordId(key);
    const clear = () => { if (generationRef.current === generation) setLoadingWordId(null); };
    const url = `${BACKEND}/api/v1/admin/word-audio/${wordId}/${filename}`;
    const audio = new Audio(url);
    audio.playbackRate = playbackRate;
    audioRef.current = audio;
    audio.addEventListener("ended", clear);
    audio.addEventListener("error", () => { clear(); alert("Failed to play word audio."); });
    audio.play().catch(() => { clear(); alert("Failed to play word audio."); });
  }, []);

  const stop = useCallback(() => {
    if (audioRef.current) { audioRef.current.pause(); audioRef.current = null; }
    generationRef.current++;
    setLoadingWordId(null);
  }, []);

  useEffect(() => () => { if (audioRef.current) audioRef.current.pause(); }, []);

  return { playWord, stop, loadingWordId };
}

/* ═══════════════════════════════════
   SVG Icons
   ═══════════════════════════════════ */

function PlayIcon({ className }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="currentColor">
      <path d="M5.25 5.653c0-.856.917-1.398 1.667-.986l11.54 6.347a1.125 1.125 0 0 1 0 1.972l-11.54 6.347a1.125 1.125 0 0 1-1.667-.986V5.653Z" />
    </svg>
  );
}

function SlowPlayIcon({ className }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
      <path d="M21 7.5V18M15 7.5V18M3 16.811V8.69c0-.864.933-1.406 1.683-.977l7.108 4.061a1.125 1.125 0 0 1 0 1.954l-7.108 4.061A1.125 1.125 0 0 1 3 16.811Z" />
    </svg>
  );
}

function SpinnerIcon({ className }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round">
      <path d="M12 2v4M12 18v4M4.93 4.93l2.83 2.83M16.24 16.24l2.83 2.83M2 12h4M18 12h4M4.93 19.07l2.83-2.83M16.24 7.76l2.83-2.83" />
    </svg>
  );
}

function BookIcon({ className }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
      <path d="M12 6.042A8.967 8.967 0 0 0 6 3.75c-1.052 0-2.062.18-3 .512v14.25A8.987 8.987 0 0 1 6 18c2.305 0 4.408.867 6 2.292m0-14.25a8.966 8.966 0 0 1 6-2.292c1.052 0 2.062.18 3 .512v14.25A8.987 8.987 0 0 0 18 18a8.967 8.967 0 0 0-6 2.292m0-14.25v14.25" />
    </svg>
  );
}

function SparkIcon({ className }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
      <path d="M9.813 15.904 9 18.75l-.813-2.846a4.5 4.5 0 0 0-3.09-3.09L2.25 12l2.846-.813a4.5 4.5 0 0 0 3.09-3.09L9 5.25l.813 2.846a4.5 4.5 0 0 0 3.09 3.09l2.847.814-2.846.813a4.5 4.5 0 0 0-3.09 3.09ZM18.259 8.715 18 9.75l-.259-1.035a3.375 3.375 0 0 0-2.455-2.456L14.25 6l1.036-.259a3.375 3.375 0 0 0 2.455-2.456L18 2.25l.259 1.035a3.375 3.375 0 0 0 2.456 2.456L21.75 6l-1.035.259a3.375 3.375 0 0 0-2.456 2.456Z" />
    </svg>
  );
}

function ClockIcon({ className }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
      <path d="M12 6v6h4.5m4.5 0a9 9 0 1 1-18 0 9 9 0 0 1 18 0Z" />
    </svg>
  );
}

const CONFETTI_COLORS = ["#818cf8", "#6366f1", "#d4a843", "#40916c", "#e8b4b8"];

/* ═══════════════════════════════════
   Word Detail Card
   ═══════════════════════════════════ */

function WordDetail({ wordData, onClose, onPlayWord, onPlayExample, loadingWordId, isSaved, onSave, onRemove, sparkleWordId }) {
  const wordId = wordData.id;
  const hasAudio = !!wordId;
  const isWordLoading = loadingWordId === `${wordId}/word_in_swedish.mp3`;
  const isExampleLoading = loadingWordId === `${wordId}/example_in_swedish.mp3`;
  const saved = isSaved?.(wordId);
  const [removeConfirm, setRemoveConfirm] = useState(false);
  const [showAddedToast, setShowAddedToast] = useState(false);

  return (
    <div className={s.wordDetail}>
      <button className={s.wordDetailCloseBtn} onClick={() => { setRemoveConfirm(false); onClose(); }} title="Close">
        <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round">
          <path d="M6 18 18 6M6 6l12 12" />
        </svg>
      </button>
      <div className={s.wordDetailHero}>
        <div className={s.wordDetailHeroText}>
          <span className={s.wordDetailWord}>{wordData.word}</span>
          <span className={s.wordDetailDivider} />
          <span className={s.wordDetailMeaning}>{wordData.meaning}</span>
        </div>
        {onSave && (
          <div className={s.wordDetailSaveWrap}>
            <button
              className={`${s.wordDetailSaveCircle} ${saved ? s.wordDetailSaveCircleSaved : ""} ${showAddedToast ? s.wordDetailSaveCircleJustSaved : ""}`}
              onClick={() => {
                if (saved) {
                  setRemoveConfirm(true);
                } else {
                  onSave(wordId);
                  setShowAddedToast(true);
                  setTimeout(() => setShowAddedToast(false), 2000);
                }
              }}
              title={saved ? "Remove from flashcards" : "Add to flashcards"}
            >
              {showAddedToast ? (
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M4.5 12.75l6 6 9-13.5" />
                </svg>
              ) : saved ? (
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round">
                  <path d="M5 12h14" />
                </svg>
              ) : (
                <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round">
                  <path d="M12 5v14M5 12h14" />
                </svg>
              )}
            </button>
            {sparkleWordId === String(wordId) && (
              <span className={s.sparkleRing} aria-hidden="true" />
            )}
            {showAddedToast && (
              <span className={s.addedToast}>Added to flashcards!</span>
            )}
          </div>
        )}
      </div>
      {removeConfirm && (
        <div className={s.removeConfirm}>
          <span className={s.removeConfirmText}>
            Remove from flashcard memory?
          </span>
          <div className={s.removeConfirmBtns}>
            <button className={s.removeConfirmBtn} onClick={() => setRemoveConfirm(false)}>
              Keep
            </button>
            <button
              className={`${s.removeConfirmBtn} ${s.removeConfirmBtnDanger}`}
              onClick={() => { onRemove(wordId); setRemoveConfirm(false); }}
            >
              Remove
            </button>
          </div>
        </div>
      )}
      <div className={s.wordDetailAudioRow}>
        <button
          className={s.wordDetailAudioBtn}
          title="Play word"
          disabled={!hasAudio || isWordLoading}
          onClick={() => onPlayWord(wordId, 1.0)}
        >
          {isWordLoading
            ? <SpinnerIcon className={`${s.wordDetailAudioIcon} ${s.spinning}`} />
            : <PlayIcon className={s.wordDetailAudioIcon} />}
        </button>
        <button
          className={s.wordDetailAudioBtn}
          title="Slow play"
          disabled={!hasAudio || isWordLoading}
          onClick={() => onPlayWord(wordId, 0.7)}
        >
          {isWordLoading
            ? <SpinnerIcon className={`${s.wordDetailAudioIcon} ${s.spinning}`} />
            : <SlowPlayIcon className={s.wordDetailAudioIcon} />}
        </button>
      </div>
      <p className={s.wordDetailDesc}>{wordData.description}</p>
      <div className={s.wordDetailExample}>
        <div className={s.wordDetailExampleHeader}>
          <span className={s.wordDetailExampleLabel}>Exempel</span>
          <button
            className={s.wordDetailPlayBtn}
            title="Play example"
            disabled={!hasAudio || isExampleLoading}
            onClick={() => onPlayExample(wordId, 1.0)}
          >
            {isExampleLoading
              ? <SpinnerIcon className={`${s.wordDetailPlayIcon} ${s.spinning}`} />
              : <PlayIcon className={s.wordDetailPlayIcon} />}
          </button>
        </div>
        <p className={s.wordDetailExampleSv}>{wordData.example}</p>
        <p className={s.wordDetailExampleEn}>{wordData.exampleTranslation}</p>
      </div>
    </div>
  );
}

/* ═══════════════════════════════════
   Swipe hook
   ═══════════════════════════════════ */

function useSwipe(onSwipeLeft, onSwipeRight) {
  const touchStart = useRef(null);
  const touchEnd = useRef(null);
  const minSwipeDistance = 50;

  const onTouchStart = useCallback((e) => {
    touchEnd.current = null;
    touchStart.current = e.targetTouches[0].clientX;
  }, []);

  const onTouchMove = useCallback((e) => {
    touchEnd.current = e.targetTouches[0].clientX;
  }, []);

  const onTouchEnd = useCallback(() => {
    if (!touchStart.current || !touchEnd.current) return;
    const distance = touchStart.current - touchEnd.current;
    if (Math.abs(distance) >= minSwipeDistance) {
      if (distance > 0) onSwipeLeft();
      else onSwipeRight();
    }
    touchStart.current = null;
    touchEnd.current = null;
  }, [onSwipeLeft, onSwipeRight]);

  return { onTouchStart, onTouchMove, onTouchEnd };
}

/* ═══════════════════════════════════
   Main Component
   ═══════════════════════════════════ */

export default function StoryRecap({ onBack, onAskAi, storyId, moduleId, onComplete }) {
  const [storyData, setStoryData] = useState(null);
  const sentences = useMemo(() => storyData?.sentences || [], [storyData]);

  const [moduleStatus, setModuleStatus] = useState("idle");
  const [isStarting, setIsStarting] = useState(false);
  const [startError, setStartError] = useState(null);
  const [isCompleting, setIsCompleting] = useState(false);

  const [currentIndex, setCurrentIndex] = useState(-1);
  const [expandedWord, setExpandedWord] = useState(null);
  const [hoveredWord, setHoveredWord] = useState(null);
  const [slideDirection, setSlideDirection] = useState(null);
  const [animating, setAnimating] = useState(false);
  const [avatarOk, setAvatarOk] = useState(true);
  const [showCinema, setShowCinema] = useState(false);
  const [initialSavedWordIds, setInitialSavedWordIds] = useState([]);

  const { isSaved, saveWord, removeWord, sparkleWordId, savedCount } = useWordSave(storyId, initialSavedWordIds);

  // Cover-page stats
  const interactiveWordCount = useMemo(() => {
    const ids = new Set();
    sentences.forEach((sent) => sent.words?.forEach((w) => { if (w.id != null) ids.add(w.id); }));
    return ids.size;
  }, [sentences]);
  const estMinutes = Math.max(1, Math.round(sentences.length * 0.5));

  // Celebration confetti — randomized once per mount
  const confettiPieces = useMemo(() =>
    Array.from({ length: 40 }, (_, i) => ({
      left: Math.random() * 100,
      delay: Math.random() * 0.9,
      duration: 2.6 + Math.random() * 1.8,
      color: CONFETTI_COLORS[i % CONFETTI_COLORS.length],
      size: 6 + Math.random() * 6,
      rotate: Math.random() * 360,
      drift: -50 + Math.random() * 100,
    })), []);

  const { setModuleContext } = useAiChat();

  // Auto-play state
  const [autoPlayStatus, setAutoPlayStatus] = useState("idle");
  const [svVisibleCount, setSvVisibleCount] = useState(0);
  const [phase, setPhase] = useState("idle");

  // Audio hooks
  const { play: playSentenceAudio, pause: pauseSentenceAudio, resume: resumeSentenceAudio, stop: stopSentenceAudio, playing: sentencePlaying, isPaused: sentencePaused } = useAudioPlayer();
  const { playWord, stop: stopWord, loadingWordId } = useWordAudio();

  // Refs for auto-play sequencer
  const sequencerAudioRef = useRef(null);
  const timerRef = useRef(null);
  const svIntervalRef = useRef(null);
  const mountedRef = useRef(true);
  const pausedRef = useRef(false);
  const pauseResolveRef = useRef(null);
  const currentIndexRef = useRef(currentIndex);
  const abortRef = useRef(null);

  useEffect(() => { currentIndexRef.current = currentIndex; }, [currentIndex]);
  useEffect(() => {
    mountedRef.current = true;
    return () => { mountedRef.current = false; };
  }, []);

  // Fetch story data on mount so idle screen can show the topic
  useEffect(() => {
    if (!storyId || !moduleId) return;
    setIsStarting(true);
    setStartError(null);
    apiFetch(`/api/v1/dashboard/modules/${storyId}/${moduleId}/start`, { method: "PATCH" })
      .then(res => {
        if (!res.ok) throw new Error("Could not load module");
        return res.json();
      })
      .then(data => {
        setStoryData({
          story_id: data.story_id,
          topic: data.topic,
          sentences: data.sentences,
          are_words_info_generated: data.are_words_info_generated,
        });
        if (data.saved_word_ids) setInitialSavedWordIds(data.saved_word_ids);
      })
      .catch(err => setStartError(err.message))
      .finally(() => setIsStarting(false));
  }, [storyId, moduleId]);

  // Start learning — jump to first sentence
  const handleStart = useCallback(() => {
    setCurrentIndex(0);
    setModuleStatus("playing");
  }, []);

  // Finish module
  const handleFinish = useCallback(async () => {
    if (!storyId || !moduleId) { onComplete?.(); onBack(); return; }
    setIsCompleting(true);
    try {
      const res = await apiFetch(`/api/v1/dashboard/modules/${storyId}/${moduleId}/complete`, { method: "PATCH" });
      if (!res.ok) throw new Error("Could not mark module complete");
      const data = await res.json();
      setIsCompleting(false);
      if (data.feeling) {
        onComplete?.();
        onBack();
      } else {
        setModuleStatus("feedback");
      }
    } catch {
      setIsCompleting(false);
      setModuleStatus("feedback");
    }
  }, [storyId, moduleId, onComplete, onBack]);

  const handleFeedbackDone = useCallback(() => {
    onComplete?.();
    onBack();
  }, [onComplete, onBack]);

  // Progress
  const totalSteps = sentences.length;
  const currentStep = currentIndex;

  const isLastSentence = currentIndex === sentences.length - 1;
  const currentSentence = sentences[currentIndex] || null;

  // Build audio path
  const getAudioPath = useCallback((idx) => {
    const sent = sentences[idx];
    if (!sent) return null;
    return `sentences/${sent.id}/sentence_in_swedish.mp3`;
  }, [sentences]);

  // Swedish words
  const svText = currentSentence?.in_swedish || "";
  const svWords = svText.split(/\s+/).filter(Boolean);

  // Cleanup helpers
  const stopSequencerAudio = useCallback(() => {
    if (sequencerAudioRef.current) {
      sequencerAudioRef.current.pause();
      sequencerAudioRef.current.onended = null;
      sequencerAudioRef.current.onloadedmetadata = null;
      sequencerAudioRef.current.onerror = null;
      sequencerAudioRef.current = null;
    }
  }, []);

  const stopTimers = useCallback(() => {
    if (timerRef.current) { clearTimeout(timerRef.current); timerRef.current = null; }
    if (svIntervalRef.current) { clearInterval(svIntervalRef.current); svIntervalRef.current = null; }
  }, []);

  const abortSequencer = useCallback(() => {
    if (abortRef.current) { abortRef.current.abort(); abortRef.current = null; }
    stopSequencerAudio();
    stopTimers();
    pauseResolveRef.current = null;
  }, [stopSequencerAudio, stopTimers]);

  useEffect(() => () => {
    abortSequencer();
    stopSentenceAudio();
    stopWord();
  }, [abortSequencer, stopSentenceAudio, stopWord]);

  // Word reveal helpers
  const startWordReveal = useCallback((totalWords, durationMs) => {
    if (svIntervalRef.current) clearInterval(svIntervalRef.current);
    if (totalWords <= 0) { setSvVisibleCount(0); return; }

    setSvVisibleCount(1);
    if (totalWords === 1) return;

    const revealDuration = durationMs * 0.8;
    const interval = Math.max(revealDuration / (totalWords - 1), 80);
    let count = 1;

    svIntervalRef.current = setInterval(() => {
      count++;
      setSvVisibleCount(count);
      if (count >= totalWords) {
        clearInterval(svIntervalRef.current);
        svIntervalRef.current = null;
      }
    }, interval);
  }, []);

  // Pause gate
  const checkPaused = useCallback((signal) => {
    if (signal?.aborted) return Promise.reject(new DOMException("Aborted", "AbortError"));
    if (!pausedRef.current) return Promise.resolve();
    return new Promise((resolve, reject) => {
      pauseResolveRef.current = resolve;
      signal?.addEventListener("abort", () => reject(new DOMException("Aborted", "AbortError")), { once: true });
    });
  }, []);

  // Core sequencer
  const playStepAudio = useCallback((url, signal) => {
    return new Promise((resolve, reject) => {
      stopSequencerAudio();
      if (signal?.aborted) { reject(new DOMException("Aborted", "AbortError")); return; }

      const audio = new Audio(url);
      sequencerAudioRef.current = audio;

      const onAbort = () => {
        audio.pause();
        audio.onended = null;
        audio.onloadedmetadata = null;
        audio.onerror = null;
        reject(new DOMException("Aborted", "AbortError"));
      };
      signal?.addEventListener("abort", onAbort, { once: true });

      audio.onloadedmetadata = () => {
        resolve(audio.duration * 1000);
      };
      audio.onerror = () => reject(new Error("Audio failed"));
      audio.play().catch(reject);
    });
  }, [stopSequencerAudio]);

  const runStep = useCallback(async (stepIdx, signal) => {
    if (!mountedRef.current || signal?.aborted) return;

    const audioStoryId = storyData?.story_id;
    if (!audioStoryId) return;

    const sent = sentences[stepIdx];
    if (!sent) return;
    const audioPath = `sentences/${sent.id}/sentence_in_swedish.mp3`;
    const audioUrl = `${BACKEND}/api/v1/admin/audio/${storyId}/${audioPath}`;

    const text = sent.in_swedish || "";
    const wordCount = text.split(/\s+/).filter(Boolean).length;

    setSvVisibleCount(0);

    await checkPaused(signal);
    if (!mountedRef.current || signal?.aborted) return;
    setPhase("sv-audio");

    try {
      const dur = await playStepAudio(audioUrl, signal);
      if (!mountedRef.current || signal?.aborted) return;

      startWordReveal(wordCount, dur);

      await new Promise((resolve, reject) => {
        if (!sequencerAudioRef.current) { resolve(); return; }
        sequencerAudioRef.current.onended = resolve;
        signal?.addEventListener("abort", () => reject(new DOMException("Aborted", "AbortError")), { once: true });
      });
    } catch (e) {
      if (e?.name === "AbortError") throw e;
    }
    if (!mountedRef.current || signal?.aborted) return;

    if (svIntervalRef.current) { clearInterval(svIntervalRef.current); svIntervalRef.current = null; }
    setSvVisibleCount(wordCount);

    await checkPaused(signal);
    if (!mountedRef.current || signal?.aborted) return;
    setPhase("step-pause");
    await new Promise((resolve, reject) => {
      timerRef.current = setTimeout(resolve, PAUSE_BETWEEN_STEPS);
      signal?.addEventListener("abort", () => {
        clearTimeout(timerRef.current);
        reject(new DOMException("Aborted", "AbortError"));
      }, { once: true });
    });
    if (!mountedRef.current || signal?.aborted) return;

    const maxIdx = sentences.length - 1;
    if (stepIdx >= maxIdx) {
      setAutoPlayStatus("idle");
      setPhase("idle");
    } else {
      const nextIdx = stepIdx + 1;
      setCurrentIndex(nextIdx);
      currentIndexRef.current = nextIdx;
      setExpandedWord(null);
      await runStep(nextIdx, signal);
    }
  }, [storyData, storyId, sentences, checkPaused, playStepAudio, startWordReveal]);

  // Start auto-play
  const startAutoPlay = useCallback((fromIdx) => {
    abortSequencer();
    pausedRef.current = false;
    const controller = new AbortController();
    abortRef.current = controller;
    setAutoPlayStatus("playing");
    setExpandedWord(null);
    runStep(fromIdx, controller.signal).catch((e) => {
      if (e?.name !== "AbortError") console.error("Sequencer error:", e);
    });
  }, [abortSequencer, runStep]);

  // Navigation
  const navigateTo = useCallback((newIndex, direction, shouldAutoPlay = false) => {
    if (animating) return;
    const wasPlaying = autoPlayStatus === "playing";

    abortSequencer();
    stopSentenceAudio();
    stopWord();
    setExpandedWord(null);
    setHoveredWord(null);
    setPhase("idle");

    if (!shouldAutoPlay && !wasPlaying) {
      setAutoPlayStatus("idle");
    }

    setSlideDirection(direction);
    setAnimating(true);

    const text = sentences[newIndex]?.in_swedish || "";
    const wordCount = text.split(/\s+/).filter(Boolean).length;

    setTimeout(() => {
      setCurrentIndex(newIndex);
      currentIndexRef.current = newIndex;
      setSvVisibleCount(wordCount);
      setAnimating(false);

      if (wasPlaying || shouldAutoPlay) {
        setTimeout(() => {
          startAutoPlay(newIndex);
        }, 50);
      }
    }, 200);
  }, [animating, autoPlayStatus, abortSequencer, stopSentenceAudio, stopWord, sentences, startAutoPlay]);

  const goNext = useCallback(() => {
    if (currentIndex < sentences.length - 1) {
      navigateTo(currentIndex + 1, "left");
    }
  }, [currentIndex, sentences.length, navigateTo]);

  const goPrev = useCallback(() => {
    if (currentIndex > 0) {
      navigateTo(currentIndex - 1, "right");
    }
  }, [currentIndex, navigateTo]);

  // Auto-play controls
  const handleAutoPlayToggle = useCallback(() => {
    if (autoPlayStatus === "playing") {
      // Pause
      pausedRef.current = true;
      setAutoPlayStatus("paused");
      if (sequencerAudioRef.current && !sequencerAudioRef.current.paused) {
        sequencerAudioRef.current.pause();
      }
      if (timerRef.current) { clearTimeout(timerRef.current); timerRef.current = null; }
    } else if (autoPlayStatus === "paused") {
      // Resume
      pausedRef.current = false;
      setAutoPlayStatus("playing");
      if (sequencerAudioRef.current && sequencerAudioRef.current.paused && sequencerAudioRef.current.src) {
        sequencerAudioRef.current.play().catch(() => {});
      }
      if (pauseResolveRef.current) {
        pauseResolveRef.current();
        pauseResolveRef.current = null;
      }
    } else {
      // Start
      startAutoPlay(currentIndexRef.current);
    }
  }, [autoPlayStatus, startAutoPlay]);

  // Word interaction
  const handleWordClick = useCallback((wordData) => {
    setExpandedWord((prev) => {
      if (prev && prev.id === wordData.id) return null;
      return wordData;
    });
  }, []);

  const handlePlayWord = useCallback((wordId, rate = 1.0) => {
    playWord(wordId, "word_in_swedish.mp3", rate);
  }, [playWord]);

  const handlePlayExample = useCallback((wordId, rate = 1.0) => {
    playWord(wordId, "example_in_swedish.mp3", rate);
  }, [playWord]);

  // AI chat context
  useEffect(() => {
    const ctx = {
      origin: "story",
      story_topic: storyData?.topic
        ? { in_swedish: storyData.topic.in_swedish, in_english: storyData.topic.in_english }
        : undefined,
    };
    if (currentSentence) {
      ctx.sentence = {
        in_swedish: currentSentence.in_swedish,
        in_english: currentSentence.in_english,
      };
    }
    if (expandedWord) {
      ctx.word = {
        word: expandedWord.word,
        meaning: expandedWord.meaning,
        description: expandedWord.description,
        example: expandedWord.example,
        exampleTranslation: expandedWord.exampleTranslation,
      };
    }
    setModuleContext(ctx);
  }, [storyData, currentSentence, expandedWord, setModuleContext]);

  const playCurrentSentence = useCallback((rate = 1.0) => {
    const path = getAudioPath(currentIndex);
    if (path) playSentenceAudio(storyData.story_id, path, rate);
  }, [currentIndex, getAudioPath, playSentenceAudio, storyData]);

  // Keyboard navigation
  useEffect(() => {
    if (moduleStatus !== "playing") return;
    const handler = (e) => {
      const tag = e.target.tagName;
      if (tag === "INPUT" || tag === "TEXTAREA" || e.target.isContentEditable) return;
      if (e.key === "ArrowRight" || e.key === "ArrowDown") { e.preventDefault(); goNext(); }
      if (e.key === "ArrowLeft" || e.key === "ArrowUp") { e.preventDefault(); goPrev(); }
      if (e.key === " ") { e.preventDefault(); playCurrentSentence(1.0); }
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [moduleStatus, goNext, goPrev, playCurrentSentence]);

  // Swipe support
  const swipeHandlers = useSwipe(goNext, goPrev);

  // Build animation class
  const slideClass = animating
    ? (slideDirection === "left" ? s.slideOutLeft : s.slideOutRight)
    : (slideDirection ? (slideDirection === "left" ? s.slideInRight : s.slideInLeft) : s.slideInInitial);

  const hasWordInfo = storyData?.are_words_info_generated;
  const isAutoPlaying = autoPlayStatus === "playing";
  const isPaused = autoPlayStatus === "paused";
  const canGoNext = currentIndex < sentences.length - 1;
  const canGoPrev = currentIndex > 0;

  // ── Render: idle start screen — show topic card ──
  if (moduleStatus === "idle") {
    return (
      <div className={s.panel}>
        <div className={s.header}>
          <button className={s.backBtn} onClick={onBack} title="Close">
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round">
              <path d="M18 6 6 18M6 6l12 12" />
            </svg>
          </button>
          {onAskAi && (
            <button className={s.headerAiBtn} onClick={onAskAi} aria-label="AI tutor">
              <AiSparkleIcon className={s.headerAiBtnIcon} />
              <span className={s.headerAiBtnLabel}>AI</span>
            </button>
          )}
        </div>
        <div className={s.content}>
          {isStarting ? (
            <div className={s.startScreen}>
              <div className={s.spinner} />
              <p className={s.startDesc}>Loading story...</p>
            </div>
          ) : startError ? (
            <div className={s.startScreen}>
              <p className={s.startError}>{startError}</p>
            </div>
          ) : storyData ? (
            <div className={`${s.startScreen} ${s.coverScene}`}>
              <div className={s.coverGlow} aria-hidden="true" />
              {avatarOk && (
                <div className={s.coverAvatarWrap}>
                  <div className={s.coverAvatarHalo} aria-hidden="true" />
                  <img
                    className={s.coverAvatar}
                    src={`${BACKEND}/api/v1/admin/story-avatar/${storyId}`}
                    alt=""
                    onError={() => setAvatarOk(false)}
                  />
                </div>
              )}
              <div className={`${s.topicBadge} ${s.coverItem1}`}>Story</div>
              <h3 className={`${s.topicSwedish} ${s.coverTitle} ${s.coverItem2}`}>{storyData.topic?.in_swedish || "Story"}</h3>
              <p className={`${s.topicEnglish} ${s.coverItem3}`}>{storyData.topic?.in_english || ""}</p>
              <div className={`${s.coverMeta} ${s.coverItem4}`}>
                <span className={s.coverMetaChip}>
                  <BookIcon className={s.coverMetaIcon} />
                  {sentences.length} sentences
                </span>
                {interactiveWordCount > 0 && (
                  <span className={s.coverMetaChip}>
                    <SparkIcon className={s.coverMetaIcon} />
                    {interactiveWordCount} words to unlock
                  </span>
                )}
                <span className={s.coverMetaChip}>
                  <ClockIcon className={s.coverMetaIcon} />
                  ~{estMinutes} min
                </span>
              </div>
              <div className={`${s.coverActions} ${s.coverItem5}`}>
                <button className={`${s.startBtn} ${s.coverStartBtn}`} onClick={handleStart}>
                  Start Learning
                </button>
                <button
                  className={s.cinemaBtn}
                  onClick={() => {
                    trackEvent("story_cinema_opened", { story_id: storyId, origin: "story_module" });
                    setShowCinema(true);
                  }}
                >
                  <span className={s.cinemaBtnIcon}>
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
                      <path d="M5.25 5.653c0-.856.917-1.398 1.667-.986l11.54 6.347a1.125 1.125 0 0 1 0 1.972l-11.54 6.347a1.125 1.125 0 0 1-1.667-.986V5.653Z" />
                    </svg>
                  </span>
                  <span className={s.cinemaBtnLabel}>Watch as a film</span>
                </button>
              </div>
              <p className={`${s.startHint} ${s.coverItem6}`}>
                Tap any underlined word while reading to hear it and see its meaning
              </p>
            </div>
          ) : null}
        </div>
        {showCinema && (
          <StoryCinema storyId={storyId} moduleId={moduleId} onAskAi={onAskAi} onClose={() => setShowCinema(false)} />
        )}
      </div>
    );
  }

  // ── Render: feedback ──
  if (moduleStatus === "feedback") {
    return (
      <div className={s.panel}>
        <div className={s.header}>
          <button className={s.backBtn} onClick={handleFeedbackDone} title="Close">
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round">
              <path d="M18 6 6 18M6 6l12 12" />
            </svg>
          </button>
          {onAskAi && (
            <button className={s.headerAiBtn} onClick={onAskAi} aria-label="AI tutor">
              <AiSparkleIcon className={s.headerAiBtnIcon} />
              <span className={s.headerAiBtnLabel}>AI</span>
            </button>
          )}
        </div>
        <div className={s.content}>
          <div className={s.confetti} aria-hidden="true">
            {confettiPieces.map((p, i) => (
              <span
                key={i}
                className={s.confettiPiece}
                style={{
                  left: `${p.left}%`,
                  width: `${p.size}px`,
                  height: `${p.size * 0.45}px`,
                  background: p.color,
                  animationDelay: `${p.delay}s`,
                  animationDuration: `${p.duration}s`,
                  "--drift": `${p.drift}px`,
                  "--rot": `${p.rotate}deg`,
                }}
              />
            ))}
          </div>
          <div className={s.doneScreen}>
            <div className={s.doneIcon}>
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M5 13l4 4L19 7" />
              </svg>
            </div>
            <h3 className={s.doneTitle}>Bra jobbat!</h3>
            <p className={s.doneDesc}>You just read a whole story in Swedish.</p>
            <div className={s.doneStats}>
              <span className={s.doneStatChip}>
                <BookIcon className={s.coverMetaIcon} />
                {sentences.length} sentences
              </span>
              {savedCount > 0 && (
                <span className={s.doneStatChip}>
                  <SparkIcon className={s.coverMetaIcon} />
                  {savedCount} words saved
                </span>
              )}
            </div>
            <FeedbackCard storyId={storyId} moduleId={moduleId} onDone={handleFeedbackDone} />
          </div>
        </div>
      </div>
    );
  }

  // ── Render: playing ──
  return (
    <div className={s.panel}>
      {/* Top bar: close + progress */}
      <div className={s.topBar}>
        <button className={s.closeBtn} onClick={handleFinish} disabled={isCompleting} title="Back to modules">
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round">
            <path d="M18 6 6 18M6 6l12 12" />
          </svg>
        </button>
        <div className={s.topProgressBar}>
          <div className={s.topProgressFill} style={{ width: `${totalSteps > 0 ? ((currentStep + 1) / totalSteps) * 100 : 0}%` }} />
        </div>
        <span className={s.topCounter}>{currentStep + 1} / {totalSteps}</span>
        {onAskAi && (
          <button className={s.headerAiBtn} onClick={onAskAi} aria-label="AI tutor">
            <AiSparkleIcon className={s.headerAiBtnIcon} />
            <span className={s.headerAiBtnLabel}>AI</span>
          </button>
        )}
      </div>

      {/* Card Content — swipeable area */}
      <div className={s.content} {...swipeHandlers}>
        <div className={s.ambient} aria-hidden="true" />
        <div className={`${s.card} ${slideClass}`} key={currentIndex}>
            <div className={s.sentenceCard}>
              {/* Swedish text with interactive words */}
              <p className={s.sentenceSwedish}>
                {hasWordInfo && currentSentence?.words
                  ? svWords.map((token, i) => {
                      const wordCore = token.replace(/[^\p{L}\p{N}]/gu, "");
                      const wordData = wordCore
                        ? currentSentence.words.find((w) => w.word.toLowerCase() === wordCore.toLowerCase())
                        : null;

                      const isCurrent = isAutoPlaying && phase === "sv-audio" && i === svVisibleCount - 1;
                      const visibilityClass = isAutoPlaying ? (i < svVisibleCount ? s.wordVisible : s.wordHidden) : "";

                      if (wordData) {
                        const isExpanded = expandedWord && expandedWord.id === wordData.id;
                        return (
                          <span key={i}>
                            {i > 0 && " "}
                            <span
                              className={`${s.wordToken} ${isExpanded ? s.wordTokenActive : ""} ${visibilityClass} ${isCurrent ? s.wordCurrent : ""}`}
                              onClick={() => handleWordClick(wordData)}
                              onMouseEnter={() => setHoveredWord(wordData)}
                              onMouseLeave={() => setHoveredWord(null)}
                            >
                              {token}
                            </span>
                          </span>
                        );
                      }
                      return (
                        <span key={i}>
                          {i > 0 && " "}
                          <span className={`${visibilityClass} ${isCurrent ? s.wordCurrent : ""}`}>{token}</span>
                        </span>
                      );
                    })
                  : svWords.map((word, i) => {
                      const isCurrent = isAutoPlaying && phase === "sv-audio" && i === svVisibleCount - 1;
                      return (
                        <span key={`${currentIndex}-sv-${i}`}>
                          {i > 0 && " "}
                          <span className={`${isAutoPlaying ? (i < svVisibleCount ? s.wordVisible : s.wordHidden) : ""} ${isCurrent ? s.wordCurrent : ""}`}>
                            {word}
                          </span>
                        </span>
                      );
                    })
                }
              </p>

              {/* Audio controls below Swedish text */}
              <div className={s.audioControls}>
                {isAutoPlaying && phase === "sv-audio" && (
                  <span className={s.playingIndicator}><span /><span /><span /></span>
                )}
                {sentencePlaying ? (
                  <button className={`${s.audioIconBtn} ${s.audioIconBtnActive}`} onClick={pauseSentenceAudio} title="Pause">
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
                      <path fillRule="evenodd" d="M6.75 5.25a.75.75 0 0 1 .75.75v12a.75.75 0 0 1-1.5 0V6a.75.75 0 0 1 .75-.75Zm10.5 0a.75.75 0 0 1 .75.75v12a.75.75 0 0 1-1.5 0V6a.75.75 0 0 1 .75-.75Z" clipRule="evenodd" />
                    </svg>
                  </button>
                ) : sentencePaused ? (
                  <button className={`${s.audioIconBtn} ${s.audioIconBtnActive}`} onClick={resumeSentenceAudio} title="Resume">
                    <PlayIcon className={s.audioIconSvg} />
                  </button>
                ) : (
                  <button className={s.audioIconBtn} onClick={() => playCurrentSentence(1.0)} title="Play">
                    <PlayIcon className={s.audioIconSvg} />
                  </button>
                )}
                <button className={s.audioIconBtn} onClick={() => playCurrentSentence(0.7)} title="Slow play">
                  <SlowPlayIcon className={s.audioIconSvg} />
                </button>
              </div>

              {/* Translation — always visible, with hover highlighting */}
              <p className={s.translationText}>
                {hoveredWord && currentSentence?.in_english
                  ? (() => {
                      const enTokens = currentSentence.in_english.split(/\s+/).filter(Boolean);
                      const meaning = hoveredWord.meaning?.toLowerCase() || "";
                      return enTokens.map((token, i) => {
                        const tokenCore = token.replace(/[^\p{L}\p{N}]/gu, "").toLowerCase();
                        const isHighlighted = meaning && tokenCore === meaning;
                        return (
                          <span key={i} className={isHighlighted ? s.enWordHighlighted : ""}>
                            {i > 0 && " "}{token}
                          </span>
                        );
                      });
                    })()
                  : currentSentence?.in_english || ""}
              </p>

            </div>

            {/* Word detail — separate card below sentence card */}
            {expandedWord && (
              <WordDetail
                wordData={expandedWord}
                onClose={() => setExpandedWord(null)}
                onPlayWord={handlePlayWord}
                onPlayExample={handlePlayExample}
                loadingWordId={loadingWordId}
                isSaved={isSaved}
                onSave={(wordId) => saveWord(wordId, "recap")}
                onRemove={(wordId) => removeWord(wordId, "recap")}
                sparkleWordId={sparkleWordId}
              />
            )}
        </div>
      </div>

      {/* Navigation Footer */}
      <div className={s.navFooter}>
        <div className={s.navRow}>
          {/* Prev */}
          <button
            className={`${s.navArrow} ${!canGoPrev ? s.navArrowDisabled : ""}`}
            onClick={goPrev}
            disabled={!canGoPrev}
            title="Previous"
          >
            <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M15 18l-6-6 6-6" />
            </svg>
          </button>

          {/* Center: progress dots + counter */}
          <div className={s.navCenter}>
            <div className={s.navDots}>
              {Array.from({ length: totalSteps }, (_, i) => (
                <div
                  key={i}
                  className={`${s.navDot} ${i === currentStep ? s.navDotActive : ""} ${i < currentStep ? s.navDotDone : ""}`}
                />
              ))}
            </div>
            <div className={s.navCenterBottom}>
              <button
                className={`${s.autoPlayToggle} ${isAutoPlaying ? s.autoPlayToggleActive : ""} ${isPaused ? s.autoPlayTogglePaused : ""}`}
                onClick={handleAutoPlayToggle}
                title={isAutoPlaying ? "Pause auto-play" : isPaused ? "Resume auto-play" : "Auto-play"}
              >
                {isAutoPlaying ? (
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor">
                    <path fillRule="evenodd" d="M6.75 5.25a.75.75 0 0 1 .75.75v12a.75.75 0 0 1-1.5 0V6a.75.75 0 0 1 .75-.75Zm10.5 0a.75.75 0 0 1 .75.75v12a.75.75 0 0 1-1.5 0V6a.75.75 0 0 1 .75-.75Z" clipRule="evenodd" />
                  </svg>
                ) : (
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M5.25 5.653c0-.856.917-1.398 1.667-.986l11.54 6.347a1.125 1.125 0 0 1 0 1.972l-11.54 6.347a1.125 1.125 0 0 1-1.667-.986V5.653Z" />
                  </svg>
                )}
              </button>
            </div>
          </div>

          {/* Next / Finish */}
          {isLastSentence ? (
            <button className={s.finishBtn} onClick={handleFinish} disabled={isCompleting}>
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                <path d="M5 13l4 4L19 7" />
              </svg>
              <span>Done</span>
            </button>
          ) : (
            <button
              className={`${s.navArrow} ${!canGoNext ? s.navArrowDisabled : ""}`}
              onClick={goNext}
              disabled={!canGoNext}
              title="Next"
            >
              <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M9 18l6-6-6-6" />
              </svg>
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
